import React, { useState, useEffect } from 'react';
import {
  Save,
  RefreshCw,
  Phone,
  Mail,
  User,
  Calendar,
  FileText,
  Star,
  MapPin
} from 'lucide-react';
import { toast } from 'react-toastify';
import SettingsSlideModal from '../../../components/Modal/SettingsSlideModal';
import api from '@/utils/api/optimized-api';

interface FacebookFormField {
  id: number;
  uuid: string;
  facebook_field_key: string;
  facebook_field_label?: string;
  facebook_field_type: string; // Any field type from Facebook API
  is_required: boolean;
  field_order: number;
  mapped_crm_column?: string;
  is_active: boolean;
}

interface FacebookLeadForm {
  id: number;
  uuid: string;
  form_id: string;
  form_name: string;
  form_status: 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
  page_id: string;
  created_time?: string;
  field_mapping?: any;
  last_sync_at?: string;
  total_leads_synced: number;
  is_active: boolean;
  formFields?: FacebookFormField[];
}

interface CrmColumn {
  column_name: string;
  display_name: string;
  data_type: string;
  is_required: boolean;
  category: string;
}

interface FacebookFormFieldMappingProps {
  form: FacebookLeadForm;
  isOpen: boolean;
  onClose: () => void;
  onSave: (formId: string, fieldMapping: any) => Promise<void>;
}

const FacebookFormFieldMapping: React.FC<FacebookFormFieldMappingProps> = ({
  form,
  isOpen,
  onClose,
  onSave
}) => {
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [crmColumns, setCrmColumns] = useState<CrmColumn[]>([]);
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen && form) {
      loadCrmColumns();
      initializeMapping();
    }
  }, [isOpen, form]);

  const loadCrmColumns = async () => {
    setIsLoadingColumns(true);
    try {
      const response = await api.req({
        url: '/integrations/crm/lead-columns',
        method: api.Method.GET
      });

      const data = response;

      if (data.success && data.data && Array.isArray(data.data)) {
        // Map API response to our interface format
        const mappedColumns: CrmColumn[] = data.data.map((col: any) => ({
          column_name: col.col_name,
          display_name: col.col_label,
          data_type: col.col_type,
          is_required: col.col_required,
          category: getCategoryFromColumnName(col.col_name) // Helper to categorize columns
        }));

        setCrmColumns(mappedColumns);
      } else {
        throw new Error(data.message || 'Failed to load CRM columns');
      }
    } catch (error) {
      console.error('Error loading CRM columns:', error);
      toast.error('Failed to load CRM columns');
    } finally {
      setIsLoadingColumns(false);
    }
  };

  // Helper function to categorize columns based on column name
  const getCategoryFromColumnName = (columnName: string): string => {
    const contactFields = ['name', 'first_name', 'last_name', 'email', 'phone', 'alternate_phone', 'whatsapp_number'];
    const businessFields = ['company_name', 'job_title', 'industry', 'website'];

    if (contactFields.includes(columnName)) return 'contact';
    if (businessFields.includes(columnName)) return 'business';
    return 'custom';
  };

  const initializeMapping = () => {
    const currentMapping = form.field_mapping || {};
    const suggestedMapping: Record<string, string> = {};

    // Initialize with existing mapping
    Object.assign(suggestedMapping, currentMapping);

    // Add suggested mappings for unmapped fields
    if (form.formFields) {
      form.formFields.forEach(field => {
        if (!suggestedMapping[field.facebook_field_key]) {
          const suggestion = getSuggestedMapping(field);
          if (suggestion) {
            suggestedMapping[field.facebook_field_key] = suggestion;
          }
        }
      });
    }

    setMapping(suggestedMapping);
  };

  const getFieldTypeIcon = (fieldType: string) => {
    switch (fieldType) {
      case 'PHONE_NUMBER':
      case 'PHONE':
        return <Phone className="h-4 w-4 text-green-500" />;
      case 'EMAIL':
        return <Mail className="h-4 w-4 text-blue-500" />;
      case 'FULL_NAME':
      case 'FIRST_NAME':
      case 'LAST_NAME':
        return <User className="h-4 w-4 text-purple-500" />;
      case 'DATE_TIME':
        return <Calendar className="h-4 w-4 text-orange-500" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  const getFieldPriority = (fieldType: string) => {
    const priorityMap: Record<string, number> = {
      'PHONE_NUMBER': 100,
      'PHONE': 100,
      'EMAIL': 90,
      'FULL_NAME': 80,
      'FIRST_NAME': 75,
      'LAST_NAME': 75,
      'SHORT_ANSWER': 50,
      'MULTIPLE_CHOICE': 40,
      'CONDITIONAL': 30,
      'DATE_TIME': 20,
      'CUSTOM': 10
    };
    return priorityMap[fieldType] || 10; // Default priority for unknown types
  };

  const getSuggestedMapping = (field: FacebookFormField): string => {
    const fieldKey = field.facebook_field_key.toLowerCase();
    const fieldType = field.facebook_field_type;

    // Priority-based mapping suggestions
    if (fieldType === 'EMAIL' || fieldKey.includes('email')) return 'email';
    if (fieldType === 'PHONE_NUMBER' || fieldType === 'PHONE' || fieldKey.includes('phone')) return 'phone';
    if (fieldType === 'FULL_NAME' || fieldKey.includes('name')) return 'name';
    if (fieldType === 'FIRST_NAME' || fieldKey.includes('first_name')) return 'first_name';
    if (fieldType === 'LAST_NAME' || fieldKey.includes('last_name')) return 'last_name';
    if (fieldKey.includes('company')) return 'company_name';
    if (fieldKey.includes('job') || fieldKey.includes('title')) return 'job_title';
    if (fieldKey.includes('city')) return 'city';
    if (fieldKey.includes('state')) return 'state';
    if (fieldKey.includes('country')) return 'country';

    return '';
  };

  const handleMappingChange = (facebookFieldKey: string, crmColumn: string) => {
    setMapping(prev => ({
      ...prev,
      [facebookFieldKey]: crmColumn
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(form.form_id, mapping);
    } catch (error) {
      console.error('Error saving mapping:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Utility function for expanding/collapsing sections (currently unused)
  // const toggleSection = (section: string) => {
  //   setExpandedSections(prev => ({
  //     ...prev,
  //     [section]: !prev[section]
  //   }));
  // };

  const getCrmColumnsByCategory = (category: string) => {
    return crmColumns.filter(col => col.category === category);
  };

  const sortedFormFields = form.formFields?.sort((a, b) => {
    // Sort by priority first (higher priority first), then by field order
    const priorityDiff = getFieldPriority(b.facebook_field_type) - getFieldPriority(a.facebook_field_type);
    if (priorityDiff !== 0) return priorityDiff;
    return a.field_order - b.field_order;
  }) || [];

  if (!isOpen) return null;

  return (
    <SettingsSlideModal
      isOpen={isOpen}
      onClose={onClose}
      title="Field Mapping"
      subtitle={`Map Facebook form fields to CRM columns for ${form.form_name}`}
      icon={<MapPin className="w-6 h-6" />}
    >
      <>
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {isLoadingColumns ? (
              <div className="flex items-center justify-center h-32">
                <div className="flex items-center space-x-2">
                  <RefreshCw className="h-5 w-5 animate-spin text-blue-500" />
                  <span className="text-gray-600">Loading CRM columns...</span>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Field Mappings */}
                <div className="space-y-4">
                  {sortedFormFields.map((field) => {
                    const currentMapping = mapping[field.facebook_field_key] || '';

                    return (
                      <div
                        key={field.id}
                        className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 bg-white dark:bg-gray-800"
                      >
                        <div className="flex items-center justify-between gap-4">
                          {/* Field Name */}
                          <div className="flex items-center space-x-2 min-w-0 flex-1">
                            {getFieldTypeIcon(field.facebook_field_type)}
                            <span className="font-medium text-gray-900 dark:text-white truncate">
                              {field.facebook_field_label || field.facebook_field_key}
                            </span>
                            {field.is_required && <Star className="h-3 w-3 text-red-500 flex-shrink-0" />}
                          </div>

                          {/* Mapping Select */}
                          <div className="flex-1 max-w-xs">
                            <select
                              value={currentMapping}
                              onChange={(e) => handleMappingChange(field.facebook_field_key, e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            >
                              <option value="">-- Select Column --</option>
                              {getCrmColumnsByCategory('contact').map((column) => (
                                <option key={column.column_name} value={column.column_name}>
                                  {column.display_name}
                                </option>
                              ))}
                              {getCrmColumnsByCategory('business').map((column) => (
                                <option key={column.column_name} value={column.column_name}>
                                  {column.display_name}
                                </option>
                              ))}
                              {getCrmColumnsByCategory('custom').map((column) => (
                                <option key={column.column_name} value={column.column_name}>
                                  {column.display_name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-900">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
<div></div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {isSaving ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>{isSaving ? 'Saving...' : 'Save Mapping'}</span>
              </button>
            </div>
          </div>
        </div>
      </>
    </SettingsSlideModal>
  );
};

export default FacebookFormFieldMapping;